import React, { useState, useEffect, useRef, useId } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── design tokens ────────────────────────────────── */
const T = {
  bg: "#0C1E1C",
  deep: "#081412",
  teal: "#5B9490",
  tealDk: "#2D5C5A",
  tealLt: "#7BB5B1",
  tealBdr: "rgba(91,148,144,0.18)",
  bdr: "rgba(255,255,255,0.07)",
  w60: "rgba(255,255,255,0.60)",
  w45: "rgba(255,255,255,0.45)",
  w30: "rgba(255,255,255,0.30)",
  w20: "rgba(255,255,255,0.20)",
  w12: "rgba(255,255,255,0.12)",
};
const SPINE = 68;
const EASE = "cubic-bezier(0.25,0.46,0.45,0.94)";

/* ── hooks ────────────────────────────────────────── */
function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Starts AT the target so the value is present in the server-rendered/prerendered
// HTML (audit T5 — crawlers, link unfurlers and AI engines were reading "0").
// The count-up is a progressive enhancement layered on top after mount.
function useCountUp(target, durationMs, delayMs) {
  const [val, setVal] = useState(target);
  useEffect(() => {
    if (!target || prefersReducedMotion()) return;
    let raf;
    const tmr = setTimeout(() => {
      let t0 = null;
      const tick = (ts) => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / durationMs, 1);
        setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delayMs);
    return () => { clearTimeout(tmr); cancelAnimationFrame(raf); };
  }, []); // runs once on mount — target is stable
  return val;
}

function useMediaQuery(q) {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    setM(mq.matches);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [q]);
  return m;
}

/* ── rich text ────────────────────────────────────── */
const parseRichText = (text) => {
  if (!text) return "";
  return text
    .replace(/^## (.+)$/gm, '<span style="color:inherit;font-size:16px;font-weight:700;display:block;margin:12px 0 2px">$1</span>')
    .replace(/^### (.+)$/gm, '<span style="color:inherit;font-size:14px;font-weight:600;display:block;margin:8px 0 2px">$1</span>')
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color:inherit;font-weight:600;font-family:inherit">$1</strong>')
    .replace(/__(.*?)__/g, '<u style="color:inherit;text-decoration:underline">$1</u>')
    .replace(/\*(.*?)\*/g, '<em style="color:inherit;font-style:italic">$1</em>')
    .replace(/^- (.+)$/gm, '<li style="color:inherit;margin-bottom:4px">$1</li>')
    .replace(/((?:<li[^>]*>.*?<\/li>)\n?)+/g, (m) => '<ul style="margin:6px 0 8px 20px;padding:0;list-style:disc">' + m.replace(/\n/g, "") + "</ul>")
    .replace(/\n/g, "<br>");
};

/* ── stat parser ──────────────────────────────────── */
function parseStatValue(raw) {
  if (!raw) return null;
  const match = raw.match(/^([^0-9]*)(\d+\.?\d*)(.*)$/);
  if (!match || !match[2]) return null;
  return { prefix: match[1], num: parseFloat(match[2]), suffix: match[3] };
}

/* ── atoms ────────────────────────────────────────── */
function Tags({ tags, small }) {
  const items = (tags || []).slice(0, 6);
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", gap: small ? 6 : 8, flexWrap: "wrap" }}>
      {items.map((t) => (
        <span
          key={t}
          style={{
            fontSize: small ? 10 : 11,
            fontWeight: 500,
            padding: small ? "3px 9px" : "4px 11px",
            borderRadius: 9999,
            border: "1px solid rgba(91,148,144,0.85)",
            color: "#a8dbd8",
            fontFamily: "Montserrat, sans-serif",
            letterSpacing: "0.04em",
            background: "linear-gradient(135deg, rgba(91,148,144,0.28) 0%, rgba(45,92,90,0.18) 100%)",
            boxShadow: "0 0 10px rgba(91,148,144,0.30), inset 0 1px 0 rgba(255,255,255,0.10)",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

const REVIEW_STAR_PATH =
  "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z";

export function CaseStudyReview({ source, quote, author, designation, rating, url, small }) {
  if (!quote) return null;
  const filledStars = rating ? Math.round(rating) : 0;
  const starGradientId = `review-star-grad-${useId().replace(/:/g, "")}`;
  const linkText = source === "google" ? "Read the full review on Google →" : "Read the full review on G2 →";
  const sourceIconSrc = source === "google" ? "/google-icon.png" : "/g2-icon.png";
  const sourceIconAlt = source === "google" ? "Google" : "G2";

  return (
    <div style={{ borderLeft: `2px solid ${T.tealDk}`, paddingLeft: small ? 12 : 20, maxWidth: small ? undefined : 480 }}>
      {rating > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 3, marginBottom: small ? 6 : 10 }}>
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <linearGradient id={starGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C2E8E1" />
                <stop offset="50%" stopColor="#5B9490" />
                <stop offset="100%" stopColor="#2D5C5A" />
              </linearGradient>
            </defs>
          </svg>
          {Array.from({ length: 5 }, (_, i) => (
            <svg
              key={i}
              data-testid="review-star"
              data-filled={i < filledStars ? "true" : "false"}
              width={small ? 12 : 14}
              height={small ? 12 : 14}
              viewBox="0 0 24 24"
              fill={i < filledStars ? `url(#${starGradientId})` : "rgba(166,210,206,0.35)"}
              style={
                i < filledStars
                  ? { filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45)) drop-shadow(0 0 3px rgba(91,148,144,0.45))" }
                  : undefined
              }
              aria-hidden="true"
            >
              <path d={REVIEW_STAR_PATH} />
            </svg>
          ))}
          <img
            src={sourceIconSrc}
            alt={sourceIconAlt}
            data-testid="review-source-icon"
            style={{ height: small ? 12 : 14, width: "auto", marginLeft: "auto" }}
          />
        </div>
      )}
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: small ? 12 : 13,
          fontStyle: "italic",
          color: "#dde8e7",
          lineHeight: 1.72,
        }}
      >
        {quote}
      </div>
      {(author || url) && (
        <div style={{ marginTop: small ? 6 : 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {author && (
            <div>
              <div
                data-testid="review-author"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: small ? 10 : 11,
                  color: "rgba(255,255,255,0.65)",
                }}
              >
                {author}
              </div>
              {designation && (
                <div
                  data-testid="review-designation"
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    fontSize: small ? 9 : 10,
                    color: "rgba(255,255,255,0.42)",
                    marginTop: 2,
                  }}
                >
                  {designation}
                </div>
              )}
            </div>
          )}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                fontSize: small ? 10 : 11,
                color: "#7BB5B1",
                marginLeft: "auto",
              }}
            >
              {linkText}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function getActiveReview(project, reviews) {
  if (project.reviewSource === "g2") {
    return {
      source: "g2",
      quote: project.g2ReviewQuote,
      author: project.g2ReviewAuthor,
      designation: project.g2ReviewDesignation,
      rating: project.g2ReviewRating,
      url: project.g2ReviewUrl,
    };
  }
  if (project.reviewSource === "google") {
    const linked = (reviews || []).find((r) => r.projectId === project.id);
    return linked
      ? {
          source: "google",
          quote: linked.quote,
          author: linked.author,
          designation: linked.designation,
          rating: linked.rating,
          url: linked.url,
        }
      : { quote: undefined };
  }
  return { quote: undefined };
}

export function AnimMetric({ stat, delay, small }) {
  const parsed = parseStatValue(stat?.value || "");
  const count = useCountUp(parsed?.num || 0, 1300, (delay || 0) + 350);
  const display = parsed
    ? `${parsed.prefix}${count}${parsed.suffix}`
    : (stat?.value || "—");

  return (
    <div style={{ animation: `cs-metricSlideUp .55s ${EASE} ${delay}ms both` }}>
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: small ? 26 : 56,
          letterSpacing: "-0.035em",
          lineHeight: 1,
          color: "#b8e5e2",
          textShadow: "0 0 18px rgba(91,148,144,0.65), 0 0 40px rgba(91,148,144,0.30)",
        }}
      >
        {display}
      </div>
      <div
        style={{
          fontSize: small ? 10 : 13,
          color: "#c8dedd",
          marginTop: small ? 5 : 9,
          lineHeight: 1.5,
        }}
      >
        {stat?.label || ""}
      </div>
    </div>
  );
}

/* ── lightbox ─────────────────────────────────────── */
export function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (closeBtnRef.current) closeBtnRef.current.focus();
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setCurrent((c) => Math.max(0, c - 1));
      if (e.key === "ArrowRight") setCurrent((c) => Math.min(images.length - 1, c + 1));
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [images.length, onClose]);

  if (!images.length) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.88)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative" }}
      >
        <img
          src={images[current]}
          alt={`Image ${current + 1} of ${images.length}`}
          style={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            objectFit: "contain",
            display: "block",
            borderRadius: 4,
          }}
        />
        {/* close */}
        <button
          ref={closeBtnRef}
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: -40,
            right: 0,
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.65)",
            fontSize: 28,
            cursor: "pointer",
            lineHeight: 1,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          ×
        </button>
        {/* prev */}
        {current > 0 && (
          <button
            aria-label="Previous image"
            onClick={() => setCurrent((c) => c - 1)}
            style={{
              position: "absolute",
              top: "50%",
              left: -52,
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "white",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ‹
          </button>
        )}
        {/* next */}
        {current < images.length - 1 && (
          <button
            aria-label="Next image"
            onClick={() => setCurrent((c) => c + 1)}
            style={{
              position: "absolute",
              top: "50%",
              right: -52,
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "white",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ›
          </button>
        )}
        {/* counter */}
        {images.length > 1 && (
          <div
            style={{
              position: "absolute",
              bottom: -30,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 11,
              color: "rgba(255,255,255,0.4)",
              fontFamily: "Montserrat, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            {current + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── gallery ──────────────────────────────────────── */
export function Gallery({ images }) {
  const valid = (images || []).filter(Boolean);
  const [sel, setSel] = useState(0);
  const [fading, setFading] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("16 / 9");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!valid.length) return null;

  function pick(i) {
    if (i === sel) return;
    setFading(true);
    setAspectRatio("16 / 9");  // reset during fade so canvas doesn't jump
    setTimeout(() => { setSel(i); setFading(false); }, 180);
  }

  function handleImgLoad(e) {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(`${naturalWidth} / ${naturalHeight}`);
    }
  }

  const safe = Math.min(sel, valid.length - 1);
  const hasMultiple = valid.length > 1;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* main prominent image */}
        <div
          onClick={hasMultiple ? () => setLightboxOpen(true) : undefined}
          style={{
            aspectRatio,
            minHeight: 200,
            maxHeight: 650,
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${T.tealBdr}`,
            background: "linear-gradient(135deg, #1a2e2e 0%, #0a1414 100%)",
            position: "relative",
            opacity: fading ? 0 : 1,
            transition: "opacity .18s ease",
            cursor: hasMultiple ? "zoom-in" : "default",
          }}
        >
          <img
            src={valid[safe]}
            alt=""
            data-testid="gallery-main-img"
            onLoad={handleImgLoad}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        {/* thumbnail strip — fixed-width tiles, horizontal scroll if many */}
        {hasMultiple && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexShrink: 0,
              overflowX: "auto",
              paddingBottom: 4,
            }}
          >
            {valid.map((img, i) => (
              <div
                key={i}
                onClick={() => pick(i)}
                style={{
                  flexShrink: 0,
                  width: 88,
                  height: 58,
                  borderRadius: 6,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: `${i === safe ? 2 : 1}px solid ${i === safe ? T.teal : T.bdr}`,
                  opacity: i === safe ? 1 : 0.45,
                  transition: "all .2s ease",
                  position: "relative",
                  background: "#0a1414",
                }}
              >
                <img
                  src={img}
                  alt=""
                  data-testid="gallery-thumb-img"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {lightboxOpen && (
        <Lightbox
          images={valid}
          startIndex={safe}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

/* ── panel layout ─────────────────────────────────── */
function PanelLayout({ text, images, mobile }) {
  const valid = (images || []).filter(Boolean);
  const hasImages = valid.length > 0;

  const textBlock = text ? (
    <div
      style={{
        fontFamily: "Montserrat, sans-serif",
        fontWeight: 300,
        fontSize: mobile ? 13 : 15,
        color: T.w45,
        lineHeight: 1.8,
      }}
      dangerouslySetInnerHTML={{ __html: parseRichText(text) }}
    />
  ) : (
    <p style={{ fontFamily: "Montserrat, sans-serif", fontSize: 13, color: T.w20, fontStyle: "italic" }}>
      No content for this section yet.
    </p>
  );

  if (!hasImages) {
    return <div style={{ maxWidth: mobile ? "100%" : 640 }}>{textBlock}</div>;
  }

  if (mobile) {
    return (
      <div>
        {textBlock}
        <div style={{ marginTop: 24 }}>
          <Gallery images={valid} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "start" }}>
      <div>{textBlock}</div>
      <div style={{ height: "auto" }}>
        <Gallery images={valid} />
      </div>
    </div>
  );
}

/* ── back to portfolio link ──────────────────────────
   Shared, crawlable "back" link (audit T1) — replaces the onClick-only
   spans previously duplicated across DesktopHero, MobileHero and
   ErrorState. */
export function BackToPortfolioLink({ style, className, children }) {
  return (
    <Link to="/portfolio" className={className} style={{ textDecoration: "none", ...style }}>
      {children || "← Portfolio"}
    </Link>
  );
}

/* ── panel nav ────────────────────────────────────── */
function PanelNav({ idx, total, go, onStartProject }) {
  const LABELS = ["Problem", "Discovery", "Our Work"];
  return (
    <div
      style={{
        display: "flex",
        justifyContent: idx > 0 ? "space-between" : "flex-end",
        alignItems: "center",
        marginTop: 44,
        paddingTop: 20,
        borderTop: `1px solid ${T.bdr}`,
      }}
    >
      {idx > 0 && (
        <span
          className="cs-nav-lnk"
          onClick={() => go(idx - 1)}
          style={{
            fontSize: 13,
            color: "#9ecfcc",
            cursor: "pointer",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 400,
            padding: "7px 16px",
            borderRadius: 8,
            background: "linear-gradient(135deg, rgba(91,148,144,0.20) 0%, rgba(45,92,90,0.10) 100%)",
            boxShadow: "0 0 12px rgba(91,148,144,0.18), inset 0 1px 0 rgba(255,255,255,0.07)",
          }}
        >
          ← {LABELS[idx - 1]}
        </span>
      )}
      {idx < total - 1 ? (
        <span
          className="cs-nav-lnk"
          onClick={() => go(idx + 1)}
          style={{
            fontSize: 13,
            color: "#a8dbd8",
            cursor: "pointer",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            padding: "7px 16px",
            borderRadius: 8,
            background: "linear-gradient(135deg, rgba(91,148,144,0.24) 0%, rgba(45,92,90,0.14) 100%)",
            boxShadow: "0 0 14px rgba(91,148,144,0.22), inset 0 1px 0 rgba(255,255,255,0.08)",
          }}
        >
          {LABELS[idx + 1]} →
        </span>
      ) : (
        <button className="cs-btn-primary" onClick={onStartProject}>
          Start Your Project →
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   DESKTOP HERO
   ══════════════════════════════════════════════════ */
function DesktopHero({ project, reviews }) {
  const stats = (project.outcomeStats || []).slice(0, 3);
  const hasBanner = !!project.bannerImage;

  return (
    <div style={{ position: "relative", background: T.bg, overflow: "hidden" }}>
      {/* banner layers */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        {hasBanner ? (
          <img
            src={project.bannerImage}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(140deg, #0d2a4a 0%, #1a4d7a 35%, #2b7eb0 62%, #1e3f6a 100%)",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse 80% 55% at 55% 45%, rgba(43,126,176,0.28) 0%, transparent 65%)",
          }}
        />
        {/* watermark wordmark */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: 64,
              color: "#fff",
              letterSpacing: "0.1em",
              opacity: 0.06,
              textTransform: "uppercase",
              userSelect: "none",
            }}
          >
            {project.client || project.title}
          </span>
        </div>
        {/* readability overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(5,12,20,0.68)" }} />
      </div>

      {/* breadcrumb */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "18px 64px 0",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <BackToPortfolioLink
          className="cs-back-lnk"
          style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.38)",
            cursor: "pointer",
            fontFamily: "system-ui, sans-serif",
          }}
        />
      </div>

      {/* 65 / 35 grid */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "65fr 35fr",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        {/* LEFT */}
        <div
          style={{
            padding: "32px 64px 52px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 40,
          }}
        >
          <div>
            {project.category && (
              <span
                style={{
                  display: "inline-block",
                  marginBottom: 16,
                  padding: "5px 14px",
                  background: "linear-gradient(135deg, rgba(91,148,144,0.28) 0%, rgba(45,92,90,0.18) 100%)",
                  border: "1px solid rgba(91,148,144,0.85)",
                  borderRadius: 9999,
                  fontSize: 10,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#a8dbd8",
                  boxShadow: "0 0 12px rgba(91,148,144,0.32), inset 0 1px 0 rgba(255,255,255,0.12)",
                  whiteSpace: "nowrap",
                  animation: "cs-fadeUp .4s ease 100ms both",
                }}
              >
                {project.category}
              </span>
            )}
            <h1
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 300,
                fontSize: 42,
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
                color: "#ffffff",
                marginBottom: 12,
                animation: "cs-fadeUp .45s ease 180ms both",
              }}
            >
              {project.title}
            </h1>
            {project.client && (
              <p
                style={{
                  fontSize: 15,
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: 28,
                  animation: "cs-fadeUp .4s ease 250ms both",
                }}
              >
                {project.client}
              </p>
            )}
            <div style={{ animation: "cs-fadeUp .4s ease 320ms both" }}>
              <Tags tags={project.technologies} />
            </div>
          </div>
          {getActiveReview(project, reviews).quote && (
            <div style={{ animation: "cs-fadeUp .5s ease 480ms both" }}>
              <CaseStudyReview {...getActiveReview(project, reviews)} />
            </div>
          )}
        </div>

        {/* RIGHT — metrics */}
        <div style={{ position: "relative", borderLeft: "1px solid rgba(255,255,255,0.10)" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(6,12,20,0.48)", backdropFilter: "blur(6px)" }} />
          <div style={{ position: "relative", zIndex: 1, padding: "52px 40px" }}>
            {stats.length > 0 && (
              <>
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.38)",
                    display: "block",
                    marginBottom: 32,
                    animation: "cs-fadeIn .5s ease 220ms both",
                  }}
                >
                  What we achieved
                </span>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {stats.map((stat, i) => {
                    const delay = 350 + i * 210;
                    const lineDelay = delay + 350;
                    return (
                      <React.Fragment key={i}>
                        <div style={{ padding: i === 0 ? "0 0 24px" : i === stats.length - 1 ? "24px 0 0" : "24px 0" }}>
                          <AnimMetric stat={stat} delay={delay} />
                        </div>
                        {i < stats.length - 1 && (
                          <div
                            style={{
                              height: 1,
                              background: "rgba(255,255,255,0.12)",
                              animation: `cs-metricLine .4s ease ${lineDelay}ms both`,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
            {stats.length === 0 && (
              <span style={{ fontSize: 13, color: T.w30, fontFamily: "Montserrat, sans-serif", fontStyle: "italic" }}>
                Results coming soon.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   DESKTOP BOOKSHELF
   ══════════════════════════════════════════════════ */
function DesktopBookshelf({ project, projectId, onStartProject }) {
  const SHELF_KEY = `shelf-${projectId}`;
  const [active, setActive] = useState(() => {
    const s = parseInt(localStorage.getItem(SHELF_KEY) || "0", 10);
    return s >= 0 && s <= 2 ? s : 0;
  });
  const ref = useRef(null);
  const [W, setW] = useState(1280);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function go(i) {
    setActive(i);
    localStorage.setItem(SHELF_KEY, String(i));
  }

  const activeW = Math.max(560, W - SPINE * 2);
  const PANELS = [
    { id: "problem", num: "01", title: "Problem", text: project.problem, images: project.problemImages },
    { id: "discovery", num: "02", title: "Discovery", text: project.discovery, images: project.discoveryImages },
    { id: "work", num: "03", title: "Our Work", text: project.ourWork, images: project.ourWorkImages },
  ];

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        overflow: "hidden",
        alignItems: "stretch",
        minHeight: 560,
        animation: "cs-shelfIn .5s ease .15s both",
        background: T.bg,
      }}
    >
      {PANELS.map((panel, i) => {
        const on = active === i;
        const w = on ? activeW : SPINE;
        return (
          <div
            key={panel.id}
            style={{
              position: "relative",
              overflow: "hidden",
              flexShrink: 0,
              width: w,
              minWidth: w,
              transition: `width .52s ${EASE}, min-width .52s ${EASE}`,
              borderRight: i < PANELS.length - 1 ? `1px solid ${T.bdr}` : "none",
            }}
          >
            {/* spine */}
            <div
              className="cs-spine-panel"
              onClick={() => go(i)}
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg, rgba(91,148,144,0.18) 0%, #081412 52%, rgba(91,148,144,0.10) 100%)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), inset 0 0 24px rgba(91,148,144,0.08)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "36px 0 32px",
                opacity: on ? 0 : 1,
                pointerEvents: on ? "none" : "auto",
                transition: `opacity .18s ease ${on ? "0s" : ".28s"}`,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  color: "#6db8b4",
                  textShadow: "0 0 8px rgba(91,148,144,0.50)",
                }}
              >
                {panel.num}
              </span>
              <span
                className="cs-sp-title"
                style={{
                  fontSize: 12,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 300,
                  color: "#9ecfcc",
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  transition: "color .2s ease",
                  textShadow: "0 0 10px rgba(91,148,144,0.40)",
                }}
              >
                {panel.title}
              </span>
              <span className="cs-sp-arrow" style={{ fontSize: 10, color: T.tealLt, opacity: 0, transition: "opacity .2s ease" }}>
                +
              </span>
            </div>

            {/* content */}
            <div
              style={{
                padding: "52px 56px",
                minWidth: activeW,
                opacity: on ? 1 : 0,
                pointerEvents: on ? "auto" : "none",
                transition: `opacity .32s ease ${on ? ".28s" : "0s"}`,
              }}
            >
              <PanelLayout text={panel.text} images={panel.images} mobile={false} />
              <PanelNav idx={i} total={PANELS.length} go={go} onStartProject={onStartProject} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MOBILE HERO
   ══════════════════════════════════════════════════ */
function MobileHero({ project, reviews }) {
  const stats = (project.outcomeStats || []).slice(0, 3);
  const hasBanner = !!project.bannerImage;

  return (
    <div style={{ position: "relative", background: T.bg, overflow: "hidden" }}>
      {/* banner layers */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        {hasBanner ? (
          <img
            src={project.bannerImage}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(140deg, #0d2a4a 0%, #1a4d7a 35%, #2b7eb0 62%, #1e3f6a 100%)",
            }}
          />
        )}
        <div style={{ position: "absolute", inset: 0, background: "rgba(5,12,20,0.70)" }} />
      </div>

      {/* breadcrumb */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderBottom: "rgba(255,255,255,0.08)",
        }}
      >
        <BackToPortfolioLink className="cs-back-lnk" style={{ fontSize: 12, color: T.w20, cursor: "pointer" }} />
      </div>

      {/* hero content */}
      <div style={{ position: "relative", zIndex: 1, padding: "24px 20px 20px", animation: "cs-fadeUp .4s ease both" }}>
        {project.category && (
          <span
            style={{
              display: "inline-block",
              marginBottom: 12,
              padding: "4px 12px",
              background: "linear-gradient(135deg, rgba(91,148,144,0.28) 0%, rgba(45,92,90,0.18) 100%)",
              border: "1px solid rgba(91,148,144,0.85)",
              borderRadius: 9999,
              fontSize: 10,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "#a8dbd8",
              boxShadow: "0 0 10px rgba(91,148,144,0.30), inset 0 1px 0 rgba(255,255,255,0.10)",
              whiteSpace: "nowrap",
            }}
          >
            {project.category}
          </span>
        )}
        <h1
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: 28,
            letterSpacing: "-0.025em",
            lineHeight: 1.12,
            color: "#fff",
            marginBottom: 10,
          }}
        >
          {project.title}
        </h1>
        {project.client && (
          <p style={{ fontSize: 13, color: T.w30, marginBottom: 20 }}>{project.client}</p>
        )}
        <Tags tags={project.technologies} small />
      </div>

      {/* metrics strip */}
      {stats.length > 0 && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            background: "rgba(6,12,20,0.45)",
            backdropFilter: "blur(8px)",
            borderTop: `1px solid ${T.bdr}`,
            borderBottom: `1px solid ${T.bdr}`,
            display: "grid",
            gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              style={{
                padding: "16px 12px",
                borderRight: i < stats.length - 1 ? `1px solid ${T.bdr}` : "none",
              }}
            >
              <AnimMetric stat={stat} delay={400 + i * 160} small />
            </div>
          ))}
        </div>
      )}

      {/* Case study review — G2 or Google, whichever is selected in admin */}
      {getActiveReview(project, reviews).quote && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            padding: "16px 20px",
            borderBottom: `1px solid ${T.bdr}`,
            background: "rgba(5,12,20,0.30)",
          }}
        >
          <CaseStudyReview {...getActiveReview(project, reviews)} small />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MOBILE BOOKSHELF
   ══════════════════════════════════════════════════ */
function MobileBookshelf({ project, projectId, onStartProject }) {
  const SHELF_KEY = `shelf-${projectId}`;
  const [tab, setTab] = useState(() => {
    const s = parseInt(localStorage.getItem(SHELF_KEY) || "0", 10);
    return s >= 0 && s <= 2 ? s : 0;
  });

  function go(i) {
    setTab(i);
    localStorage.setItem(SHELF_KEY, String(i));
  }

  const PANELS = [
    { title: "Problem", text: project.problem, images: project.problemImages },
    { title: "Discovery", text: project.discovery, images: project.discoveryImages },
    { title: "Our Work", text: project.ourWork, images: project.ourWorkImages },
  ];
  const panel = PANELS[tab];

  return (
    <div style={{ background: T.bg }}>
      {/* sticky tab bar */}
      <div
        style={{
          display: "flex",
          borderBottom: `1px solid ${T.bdr}`,
          position: "sticky",
          top: 64,
          background: T.deep,
          zIndex: 10,
        }}
      >
        {PANELS.map((p, i) => (
          <div
            key={p.title}
            onClick={() => go(i)}
            style={{
              flex: 1,
              padding: "13px 4px",
              textAlign: "center",
              cursor: "pointer",
              fontSize: 11,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: i === tab ? 600 : 400,
              color: i === tab ? "#a8dbd8" : T.w30,
              background: i === tab
                ? "linear-gradient(135deg, rgba(91,148,144,0.22) 0%, rgba(45,92,90,0.12) 100%)"
                : "transparent",
              boxShadow: i === tab ? "inset 0 1px 0 rgba(255,255,255,0.07), 0 0 12px rgba(91,148,144,0.15)" : "none",
              borderBottom: `2px solid ${i === tab ? T.teal : "transparent"}`,
              transition: "all .2s ease",
              letterSpacing: "-0.01em",
            }}
          >
            {p.title}
          </div>
        ))}
      </div>

      {/* content */}
      <div key={tab} style={{ padding: "28px 20px", animation: "cs-tabSlide .3s ease both" }}>
        <PanelLayout text={panel.text} images={panel.images} mobile={true} />
        <PanelNav idx={tab} total={PANELS.length} go={go} onStartProject={onStartProject} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   LOADING / ERROR STATES
   ══════════════════════════════════════════════════ */
function LoadingState() {
  return (
    <div style={{ background: "#0C1E1C", minHeight: "100vh" }}>
      <Header />
      <div
        style={{
          paddingTop: 128,
          textAlign: "center",
          color: "rgba(255,255,255,0.3)",
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: 14,
        }}
      >
        Loading project...
      </div>
    </div>
  );
}

function ErrorState() {
  return (
    <div style={{ background: "#0C1E1C", minHeight: "100vh" }}>
      <Header />
      <div style={{ paddingTop: 128, textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.3)", fontFamily: "Montserrat, sans-serif", fontWeight: 300, marginBottom: 24 }}>
          Project not found.
        </p>
        <BackToPortfolioLink
          style={{ color: T.tealLt, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 13, cursor: "pointer" }}
        >
          ← Back to Portfolio
        </BackToPortfolioLink>
      </div>
      <Footer />
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════ */
const ProjectCaseStudy = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    axios
      .get(`${API}/portfolio/${projectId}`)
      .then((res) => {
        if (res.data.success) setProject(res.data.data);
        else setError("Project not found");
      })
      .catch(() => setError("Failed to load project"))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data?.data || []))
      .catch(() => {});
  }, []);

  const handleStartProject = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  if (isLoading) return <LoadingState />;
  if (error || !project) return <ErrorState />;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, paddingTop: 64 }}>
      <Header />

      {isMobile ? (
        <MobileHero project={project} reviews={reviews} />
      ) : (
        <DesktopHero project={project} reviews={reviews} />
      )}

      {isMobile ? (
        <MobileBookshelf project={project} projectId={projectId} onStartProject={handleStartProject} />
      ) : (
        <DesktopBookshelf project={project} projectId={projectId} onStartProject={handleStartProject} />
      )}

      <Footer />
    </div>
  );
};

export default ProjectCaseStudy;
